import { supabase } from './supabase';

export interface UserDigestPreferences {
  user_id: string;
  digests_enabled: boolean;
  skip_weekends: boolean;
  max_filings_per_docket: number;
  unsubscribe_token: string;
  created_at: string;
  updated_at: string;
}

export type UserDigestPreferencesUpdate = Partial<
  Pick<UserDigestPreferences, 'digests_enabled' | 'skip_weekends' | 'max_filings_per_docket'>
>;

const DEFAULTS: Omit<UserDigestPreferences, 'user_id' | 'unsubscribe_token' | 'created_at' | 'updated_at'> = {
  digests_enabled: true,
  skip_weekends: false,
  max_filings_per_docket: 10,
};

export async function getDigestPreferences(): Promise<UserDigestPreferences | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_digest_preferences')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching digest preferences:', error);
    return null;
  }

  if (data) return data as UserDigestPreferences;

  // No row yet — synthesize defaults without inserting. First write via
  // upsertDigestPreferences will create the row (with a generated token).
  return {
    user_id: user.id,
    unsubscribe_token: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...DEFAULTS,
  };
}

export async function upsertDigestPreferences(
  changes: UserDigestPreferencesUpdate
): Promise<UserDigestPreferences> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('user_digest_preferences')
    .upsert(
      { user_id: user.id, ...changes },
      { onConflict: 'user_id' }
    )
    .select('*')
    .single();

  if (error) throw error;
  return data as UserDigestPreferences;
}
